import { MutationData, rd, RemoteData } from '@passionware/monads';
import { promiseState } from '@passionware/platform-react';
import { Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Checkbox } from 'v5/design-system/Checkbox';
import { DialogBody, DialogHeader, DialogTitle } from 'v5/design-system/Dialog';
import {
  InputGroup2,
  InputGroup2Input,
  InputGroup2Slot
} from 'v5/design-system/InputGroup2';
import { Label } from 'v5/design-system/Label';
import { Skeleton } from 'v5/design-system/Skeleton';
import { Tabs, TabsList, TabsTrigger } from 'v5/design-system/Tabs';
import { FormDialogContent } from 'v5/patterns/forms/dialogs/FormDialogContent';
import { DialogErrorFooter } from 'v5/patterns/forms/dialogs/DialogErrorFooter';
import { FormDialogError } from 'v5/patterns/forms/dialogs/FormDialogError';
import { FormSaveButton } from 'v5/patterns/forms/dialogs/FormSaveButton';
import { cn } from 'v5/platform/dom/cn';
import {
  FeatureFlagConfigView,
  FeatureManagementActionHandler
} from 'v5/services/front/FeatureManagementService/FeatureManagementService';
import type { FeatureFlagManagerBrowseBy } from 'v5/services/front/UserPreferencesService/UserPreferencesService';
import {
  buildFeatureFlagScopeSections,
  filterFeatureFlagScopeSections,
  pickPreferredScopeKey,
  type FeatureFlagScopeKey
} from './buildFeatureFlagScopeCatalog';
import { FeatureFlagCatalog } from './FeatureFlagCatalog';
import { FeatureFlagInspector } from './FeatureFlagInspector';
import { FeatureFlagScopeCatalog } from './FeatureFlagScopeCatalog';
import { FeatureFlagScopeInspector } from './FeatureFlagScopeInspector';

export interface FeatureFlagCatalogViewProps {
  view: RemoteData<FeatureFlagConfigView>;
  onAction: FeatureManagementActionHandler;
  onSave: () => Promise<void>;
  search: string;
  onSearchChange: (search: string) => void;
  starredFeatureIds: readonly string[];
  onToggleStarred: (featureId: string) => void;
  browseBy: FeatureFlagManagerBrowseBy;
  onBrowseByChange: (browseBy: FeatureFlagManagerBrowseBy) => void;
}

const dialogLayoutClassName =
  'w-[min(96vw,72rem)] max-w-[min(96vw,72rem)] h-[min(96vh,58rem)] has-[[data-has-top-shadow]]:[&_.feature-flag-dialog-title]:shadow-sm';

export function FeatureFlagCatalogView({
  view,
  onAction,
  onSave,
  search,
  onSearchChange,
  starredFeatureIds,
  onToggleStarred,
  browseBy,
  onBrowseByChange
}: FeatureFlagCatalogViewProps) {
  const saveMutation = promiseState.useMutation(onSave);
  const [selectedFeatureId, setSelectedFeatureId] = useState<string | null>(
    null
  );
  const [selectedScopeKey, setSelectedScopeKey] =
    useState<FeatureFlagScopeKey | null>(null);
  const [detailSearch, setDetailSearch] = useState('');
  const [backendAck, setBackendAck] = useState(false);

  return rd
    .journey(view)
    .wait(
      <div className={cn(dialogLayoutClassName, 'flex flex-col')}>
        <div className="space-y-4 p-6">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      </div>
    )
    .catch(e => (
      <FormDialogError
        error={e}
        title="Feature flag error"
        description="Failed to load feature flag configuration"
      />
    ))
    .map(configView => (
      <FeatureFlagCatalogViewReady
        view={configView}
        onAction={onAction}
        mutation={saveMutation.state}
        onSave={() => {
          void saveMutation.track(void 0);
        }}
        search={search}
        onSearchChange={onSearchChange}
        starredFeatureIds={starredFeatureIds}
        onToggleStarred={onToggleStarred}
        browseBy={browseBy}
        onBrowseByChange={onBrowseByChange}
        selectedFeatureId={selectedFeatureId}
        onSelectFeatureId={setSelectedFeatureId}
        selectedScopeKey={selectedScopeKey}
        onSelectScopeKey={setSelectedScopeKey}
        detailSearch={detailSearch}
        onDetailSearchChange={setDetailSearch}
        backendAck={backendAck}
        onBackendAckChange={setBackendAck}
      />
    ));
}

function FeatureFlagCatalogViewReady({
  view,
  onAction,
  mutation,
  onSave,
  search,
  onSearchChange,
  starredFeatureIds,
  onToggleStarred,
  browseBy,
  onBrowseByChange,
  selectedFeatureId,
  onSelectFeatureId,
  selectedScopeKey,
  onSelectScopeKey,
  detailSearch = '',
  onDetailSearchChange = () => undefined,
  backendAck,
  onBackendAckChange
}: {
  view: FeatureFlagConfigView;
  onAction: FeatureManagementActionHandler;
  mutation: MutationData<unknown, unknown>;
  onSave: () => void;
  search: string;
  onSearchChange: (search: string) => void;
  starredFeatureIds: readonly string[];
  onToggleStarred: (featureId: string) => void;
  browseBy: FeatureFlagManagerBrowseBy;
  onBrowseByChange: (browseBy: FeatureFlagManagerBrowseBy) => void;
  selectedFeatureId: string | null;
  onSelectFeatureId: (featureId: string) => void;
  selectedScopeKey: FeatureFlagScopeKey | null;
  onSelectScopeKey: (scopeKey: FeatureFlagScopeKey) => void;
  detailSearch: string;
  onDetailSearchChange: (search: string) => void;
  backendAck: boolean;
  onBackendAckChange: (acked: boolean) => void;
}) {
  const groups = view.featureFlagView.groups;
  const workspaceColumns = view.featureFlagView.workspaceColumns;
  const allEntries = useMemo(
    () => groups.flatMap(group => group.entries),
    [groups]
  );

  const dirtyFeatureIds = useMemo(() => {
    return new Set([
      ...view.changedBackendFlagIds,
      ...view.changedFrontendFlagIds
    ]);
  }, [view.changedBackendFlagIds, view.changedFrontendFlagIds]);

  const scopeDirty = useMemo(
    () => ({
      changedLegacyFlagIds: view.changedLegacyFlagIds,
      changedFrontendFlagIds: view.changedFrontendFlagIds,
      changedWorkspaceFlagIds: view.changedWorkspaceFlagIds
    }),
    [
      view.changedLegacyFlagIds,
      view.changedFrontendFlagIds,
      view.changedWorkspaceFlagIds
    ]
  );

  const backendDirtyCount = view.changedBackendFlagIds.length;
  const needsBackendAck = backendDirtyCount > 0;

  useEffect(() => {
    if (!needsBackendAck) {
      onBackendAckChange(false);
    }
  }, [needsBackendAck, onBackendAckChange]);

  useEffect(() => {
    onDetailSearchChange('');
  }, [browseBy, onDetailSearchChange]);

  useEffect(() => {
    if (
      selectedFeatureId &&
      allEntries.some(entry => entry.featureId === selectedFeatureId)
    ) {
      return;
    }
    const preferred =
      allEntries.find(entry => dirtyFeatureIds.has(entry.featureId)) ??
      allEntries[0];
    onSelectFeatureId(preferred?.featureId ?? null);
  }, [allEntries, dirtyFeatureIds, onSelectFeatureId, selectedFeatureId]);

  useEffect(() => {
    const sections = filterFeatureFlagScopeSections(
      buildFeatureFlagScopeSections(groups, workspaceColumns),
      search
    );
    const preferred = pickPreferredScopeKey(
      sections,
      scopeDirty,
      selectedScopeKey
    );
    if (preferred !== selectedScopeKey && preferred != null) {
      onSelectScopeKey(preferred);
    }
  }, [
    groups,
    workspaceColumns,
    search,
    scopeDirty,
    selectedScopeKey,
    onSelectScopeKey
  ]);

  const selectedEntry =
    allEntries.find(entry => entry.featureId === selectedFeatureId) ?? null;

  const canSave = view.isDirty && (!needsBackendAck || backendAck);
  const [ackBlinkKey, setAckBlinkKey] = useState(0);

  return (
    <FormDialogContent
      disabled={!canSave}
      onSubmit={() => {
        if (canSave) {
          onSave();
          return;
        }
        if (needsBackendAck && !backendAck) {
          setAckBlinkKey(key => key + 1);
        }
      }}
      open={true}
      title="Feature flags"
      renderHeader={header => (
        <DialogHeader>
          <DialogTitle className="feature-flag-dialog-title bg-bg-default">
            {header}
          </DialogTitle>
        </DialogHeader>
      )}
      mutation={mutation}
      saveText="Save"
      className={dialogLayoutClassName}
      footerContent={
        <div className="mr-auto flex min-w-0 flex-1 flex-wrap items-center gap-3 pl-4">
          {needsBackendAck && (
            <div
              key={ackBlinkKey}
              className={cn(
                'flex min-w-0 items-center gap-2 rounded px-1 -mx-1 py-0.5',
                ackBlinkKey > 0 && 'animate-danger-blink'
              )}
            >
              <Checkbox
                id="feature-flag-backend-ack"
                checked={backendAck}
                onCheckedChange={checked =>
                  onBackendAckChange(checked === true)
                }
              />
              <Label
                htmlFor="feature-flag-backend-ack"
                className="text-sm font-normal"
              >
                I understand that{' '}
                <span className="font-semibold">
                  {backendDirtyCount}{' '}
                  {backendDirtyCount === 1 ? 'flag' : 'flags'}
                </span>{' '}
                will affect others
              </Label>
            </div>
          )}
          <DialogErrorFooter mutation={mutation} compact />
        </div>
      }
      saveButtonSlot={
        <FormSaveButton
          disabled={!canSave}
          mutation={mutation}
          saveText="Save"
        />
      }
      renderDialogBody={() => (
        <DialogBody
          renderTopShadow={false}
          renderBottomShadow={false}
          className="flex min-h-0 flex-1 flex-col overflow-hidden p-0"
        >
          <div className="flex min-h-0 flex-1">
            <div className="flex w-[min(100%,24rem)] shrink-0 flex-col border-r border-border-muted has-[[data-has-top-shadow]]:[&>.-search]:shadow-sm">
              <div className="-search z-[1] shrink-0 space-y-3 border-b border-border-muted bg-bg-default p-3">
                <Tabs
                  value={browseBy}
                  onValueChange={value =>
                    onBrowseByChange(value as FeatureFlagManagerBrowseBy)
                  }
                >
                  <TabsList variant="default" size="sm" className="w-full">
                    <TabsTrigger
                      value="feature"
                      variant="default"
                      size="sm"
                      className="flex-1"
                    >
                      By feature
                    </TabsTrigger>
                    <TabsTrigger
                      value="workspace"
                      variant="default"
                      size="sm"
                      className="flex-1"
                    >
                      By workspace
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
                <InputGroup2 size="sm">
                  <InputGroup2Slot>
                    <Search className="size-4 text-fg-muted" />
                  </InputGroup2Slot>
                  <InputGroup2Input
                    value={search}
                    onChange={event => onSearchChange(event.target.value)}
                    placeholder={
                      browseBy === 'workspace'
                        ? 'Search scopes…'
                        : 'Search flags…'
                    }
                    aria-label={
                      browseBy === 'workspace'
                        ? 'Search scopes'
                        : 'Search flags'
                    }
                  />
                </InputGroup2>
              </div>
              {browseBy === 'feature' ? (
                <FeatureFlagCatalog
                  groups={groups}
                  selectedFeatureId={selectedFeatureId}
                  onSelect={onSelectFeatureId}
                  search={search}
                  dirtyFeatureIds={dirtyFeatureIds}
                  starredFeatureIds={starredFeatureIds}
                  onToggleStarred={onToggleStarred}
                />
              ) : (
                <FeatureFlagScopeCatalog
                  groups={groups}
                  workspaceColumns={workspaceColumns}
                  selectedScopeKey={selectedScopeKey}
                  onSelect={onSelectScopeKey}
                  search={search}
                  changedLegacyFlagIds={view.changedLegacyFlagIds}
                  changedFrontendFlagIds={view.changedFrontendFlagIds}
                  changedWorkspaceFlagIds={view.changedWorkspaceFlagIds}
                />
              )}
            </div>
            {browseBy === 'feature' ? (
              <FeatureFlagInspector
                entry={selectedEntry}
                workspaceColumns={workspaceColumns}
                detailSearch={detailSearch}
                onDetailSearchChange={onDetailSearchChange}
                onAction={onAction}
              />
            ) : (
              <FeatureFlagScopeInspector
                scopeKey={selectedScopeKey}
                workspaceColumns={workspaceColumns}
                groups={groups}
                detailSearch={detailSearch}
                onDetailSearchChange={onDetailSearchChange}
                starredFeatureIds={starredFeatureIds}
                onToggleStarred={onToggleStarred}
                changedLegacyFlagIds={view.changedLegacyFlagIds}
                changedFrontendFlagIds={view.changedFrontendFlagIds}
                changedWorkspaceFlagIds={view.changedWorkspaceFlagIds}
                onAction={onAction}
              />
            )}
          </div>
        </DialogBody>
      )}
    >
      {null}
    </FormDialogContent>
  );
}
